<script lang="ts">
  import type { PageData } from './$types';
  import {
    Analytics,
    AdScripts,
    SEO,
    SiteHeader,
    SiteFooter,
    Headline,
    EmbedPreviewerLink,
    LeaderboardAd,
    Theme,
    Article,
    EndNotes,
  } from '@reuters-graphics/graphics-components';
  import pkg from '$pkg';
  import { dev } from '$app/environment';
  import { assets } from '$app/paths';
  import { page } from '$app/state';
  import { isReutersApp, isReutersDev, isReutersDotcom } from '$utils/env';
  import LogBlock from '$lib/components/dev/LogBlock.svelte';

  import Post from '$lib/Post.svelte';
  import TableOfContents from '$lib/components/TableOfContents/TableOfContents.svelte';
  import InlineHeader from './InlineHeader.svelte';
  import ClockGroup from '$lib/components/Clock/ClockGroup.svelte';

  // Styles
  import '@reuters-graphics/graphics-components/scss/main.scss';
  import '$lib/styles/global.scss';

  let { data }: { data: PageData } = $props();
  let content = $derived(data.content);
  let hash = $derived(page.url.hash);
</script>

{#if isReutersDotcom(page.url)}
  <AdScripts />
{/if}

<SEO
  baseUrl={import.meta.env.BASE_URL}
  pageUrl={page.url}
  seoTitle={content.seoTitle}
  seoDescription={content.seoDescription}
  shareTitle={content.shareTitle}
  shareDescription={content.shareDescription}
  shareImgPath={`${assets}/${content.shareImgPath}`}
  shareImgAlt={content.shareImgAlt}
  publishTime={pkg?.reuters?.graphic?.published}
  updateTime={pkg?.reuters?.graphic?.updated}
  authors={pkg?.reuters?.graphic?.authors}
/>

{#if isReutersDotcom(page.url)}
  <Analytics authors={pkg?.reuters?.graphic?.authors || []} />
{/if}

<Theme base="light">
  {#if !isReutersApp(page.url)}
    {#if isReutersDotcom(page.url) && !hash}
      <LeaderboardAd />
    {:else if isReutersDev(page.url)}
      <LogBlock level="info" message="An ad will appear here on dotcom" />
    {/if}
    <SiteHeader />
  {/if}

  <Article>
    <Headline section={content.section} hed={content.hed} width="normal">
      <div slot="crown"></div>
    </Headline>

    <ClockGroup clockSize="XS" />

    {#if page.data.posts.length > 1}
      <TableOfContents posts={page.data.posts} maxHeight={330} />
    {/if}

    {#each data.posts as post, i}
      <InlineHeader index={i} />
      <Post content={post} />
      {#if i < data.posts.length - 1}
        <div class="divider"></div>
      {/if}
    {/each}

    <EndNotes notes={content.endNotes} />
  </Article>

  {#if !isReutersApp(page.url)}
    <SiteFooter />
  {/if}
</Theme>

<!-- Only visible in dev! -->
<EmbedPreviewerLink {dev} />

<style lang="scss">
  @use '@reuters-graphics/graphics-components/dist/scss/mixins' as mixins;

  .divider {
    height: 1px;
    max-width: calc(mixins.$column-width-normal * 0.9);
    width: calc(100% - 40px);
    margin: 4rem auto;
    border-top: solid 1px var(--tr-muted-grey);
  }
</style>
